<template>
  <div v-if="!$apollo.queries.getBusinessEntityDetails.loading">
    <FlowHeading
      :title="$t(`Add your business details`)"
      :sub-title="
        $t('This information allows us to show your correct business information on your bills.')
      "
    />

    <div class="business-entity-name m-b-20">
      <WdsInput
        v-model="businessName"
        name="businessName"
        autocomplete="organization"
        :label="$t('Business entity name')"
        placeholder="Worksome"
        :error="errorBusinessName"
        required
        lg
        @focusout="v$.businessName.$touch"
      />
    </div>

    <CompanyNumberInput market="CA">
      <div class="tw-mt-2">
        <WdsInput
          v-model="businessNumber"
          name="businessNumber"
          placeholder="12345678"
          :error="errorBusinessNumber"
          required
          lg
          @focusout="v$.businessNumber.$touch"
        />
      </div>
    </CompanyNumberInput>

    <div class="sales-tax">
      <WdsRadioGroup
        v-model:checked="hasSalesTax"
        name="sales-tax"
        :label="$t(`Are you registered for GST/HST?`)"
      >
        <WdsRadio :value="false">
          <WdsText :text="$t(`No, I will be billing without GST/HST`)" />
        </WdsRadio>
        <WdsRadio :value="true">
          <WdsText :text="$t(`Yes, I have a GST/HST registration`)" />
        </WdsRadio>
      </WdsRadioGroup>
      <div class="sales-tax-number">
        <WdsInput
          v-if="hasSalesTax"
          v-model="salesTaxNumber"
          name="salesTaxNumber"
          :placeholder="$t(`GST/HST number`)"
          :error="errorVatNo"
          lg
          @focusout="v$.salesTaxNumber.$touch"
        />
      </div>
    </div>

    <div class="wds-input-group continue-button">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="callNextScreen">
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
    <div v-if="lacksRequiredFields" class="button-ghost-container">
      <WdsStack justify-center>
        <WdsButtonGhost secondary @click="skip">{{ $t('Skip and update later') }}</WdsButtonGhost>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import {
  WdsButton,
  WdsButtonGhost,
  WdsInput,
  WdsRadio,
  WdsRadioGroup,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import { helpers, required, requiredIf } from '@vuelidate/validators'
import CompanyNumberInput from '@/components/compliances/BusinessEntity/CompanyNumberInput.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import { useValidatorWithGraphQlError } from '@/composables/errors'
import { FormValidation } from '@/utils/formValidation'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import { flat } from '@/utils/tools'

export default {
  name: 'CaBusinessEntityTypeDetails',

  components: {
    ButtonSpinner,
    CompanyNumberInput,
    FlowHeading,
    WdsButton,
    WdsButtonGhost,
    WdsInput,
    WdsRadio,
    WdsRadioGroup,
    WdsStack,
    WdsText,
    // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
    WdsTooltip,
  },

  inject: ['skip', 'next'],

  setup() {
    const v$ = useVuelidate()
    const errors = ref({})

    const errorBusinessName = useValidatorWithGraphQlError(
      v$,
      errors,
      'businessName',
      'registered_company_name',
    )
    const errorBusinessNumber = useValidatorWithGraphQlError(
      v$,
      errors,
      'businessNumber',
      'company_no',
    )
    const errorVatNo = useValidatorWithGraphQlError(v$, errors, 'salesTaxNumber', 'vat_no')

    return {
      v$,
      errors,
      errorBusinessName,
      errorBusinessNumber,
      errorVatNo,
    }
  },

  data() {
    return {
      hasSalesTax: undefined,
      businessName: '',
      businessNumber: '',
      businessDetailsFinished: false,
      globalId: '',
      salesTaxNumber: '',
      isSubmitting: false,
    }
  },
  computed: {
    lacksRequiredFields() {
      return this.v$.$invalid
    },
    canSubmit() {
      return !this.isSubmitting
    },
  },

  methods: {
    async callNextScreen() {
      this.errors = {}
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }
      this.isSubmitting = true
      await this.updateBusinessEntityDetails()
      if (Object.keys(this.errors).length === 0) {
        this.next()
      } else {
        this.isSubmitting = false
      }
    },
    async updateBusinessEntityDetails() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation updatePrimaryBusinessEntity($input: UpdatePrimaryBusinessEntityInput!) {
              updatePrimaryBusinessEntity(input: $input) {
                globalId
                entityType
                isPrimary
                label
                companyNo
                companyName
                taxNo
                data
              }
            }
          `,
          variables: {
            input: {
              globalId: this.globalId, // must be accountID of current logged in user
              registered_company_name: this.businessName,
              business_entity: 'common_business',
              business_entity_data: JSON.stringify({
                gst_hst_registered: this.hasSalesTax,
              }),
              vat_no: this.hasSalesTax ? this.salesTaxNumber : '',
              company_no: this.businessNumber,
            },
          },
          context: {
            headers: {
              'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
            },
          },
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },
  },

  apollo: {
    getBusinessEntityDetails: {
      query: gql`
        query me {
          me {
            activeAccount {
              globalId
              ... on Freelancer {
                primaryBusinessEntity {
                  entityType
                  isPrimary
                  label
                  companyNo
                  companyName
                  taxNo
                  data
                }
              }
            }
          }
        }
      `,
      result({ data }) {
        const info = data.me.activeAccount.primaryBusinessEntity
        this.globalId = data.me.activeAccount.globalId
        if (info) {
          this.businessName = info.companyName ? info.companyName : ''
          this.businessNumber = info.companyNo ?? this.businessNumber
          this.businessDetailsFinished = true
          const businessData = flat(JSON.parse(info.data))
          this.hasSalesTax = !!businessData?.gst_hst_registered
          this.salesTaxNumber = info.taxNo ?? this.salesTaxNumber
        }
      },
      update: (data) => data.me,
    },
  },

  validations() {
    return {
      businessName: {
        required: helpers.withMessage($t('Please enter your business name'), required),
      },
      businessNumber: {
        required: helpers.withMessage($t('Please enter your business number'), required),
      },
      salesTaxNumber: {
        requiredIf: helpers.withMessage(
          $t('Please enter your GST/HST number'),
          requiredIf(this.hasSalesTax),
        ),
      },
    }
  },
}
</script>

<style scoped>
.sales-tax {
  margin-top: 20px;
  margin-bottom: 20px;
}

.sales-tax-number {
  margin-left: 25px;
}

:deep(.wds-radio) {
  align-items: flex-start;
}

.continue-button {
  margin-top: 24px;
  margin-bottom: 16px;
}
</style>
